import React, { useCallback, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Button,
  Typography,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  LinearProgress,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import FilterListIcon from "@material-ui/icons/FilterList";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import EditIcon from "@material-ui/icons/Edit";

import * as fb from "../../../../../../firebase/firebasejs";
import { eventSubEventsRegistry } from "../../../../../../data-store/actions/events-actions";
import {
  TIPOS_OPERACION,
  construirCambio,
  listarAtributosDisponibles,
  listarCategoriasDeModalidad,
  listarModalidades,
  resolverCategoriaLabel,
  resolverModalidadLabel,
  validarParametrosOperacion,
} from "./bulkEditLogic";

const useStyles = makeStyles(() => ({
  rowSelected: { backgroundColor: "rgba(0,0,0,0.06)" },
  headCell: { whiteSpace: "nowrap" },
  headTitleWrap: { display: "flex", alignItems: "center", gap: 6, cursor: "pointer" },
  smallSelect: { minWidth: 220 },
}));

const NOMBRE_RAMA = { V: "Varonil", F: "Femenil", M: "Mixto" };
const BATCH_SIZE = 50;
const BATCH_DELAY_MS = 1000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function safeLower(v) {
  return (v ?? "").toString().toLowerCase();
}

export default function BulkEditInscritos() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const eventDetail = useSelector((state) => state.events.eventDetail);
  const userID = useSelector((state) => state.atlete.userID);
  const atleteAccount = useSelector((state) => state.atlete.atleteAccount);
  const eventParticipantsState = useSelector((state) => state.registry.eventParticipants);
  const eventSubEventsRegistryState = useSelector(
    (state) => state.events.eventSubEventsRegistry
  );

  const eventoIndex = "evento_" + eventDetail.iDEvento;

  React.useEffect(() => {
    dispatch(eventSubEventsRegistry(eventoIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoIndex]);

  // ------------------ FILTROS ------------------
  const [filters, setFilters] = useState({
    numeroText: "",
    nombreText: "",
    correoText: "",
    modalidad: "",
    categoriaValor: "", // "rama|categoria"
    rama: "",
  });
  const [campoInspeccion, setCampoInspeccion] = useState("");
  const [inspeccionModo, setInspeccionModo] = useState("todos"); // todos | conValor | sinValor | contiene
  const [inspeccionTexto, setInspeccionTexto] = useState("");

  const [filterUI, setFilterUI] = useState({ col: null, anchorEl: null });
  const openFilter = (col) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFilterUI({ col, anchorEl: e.currentTarget });
  };
  const closeFilter = () => setFilterUI({ col: null, anchorEl: null });
  const popoverOpen = Boolean(filterUI.anchorEl);

  // ------------------ ORDEN ------------------
  const [sort, setSort] = useState({ campo: "numero", direccion: "asc" });
  const toggleSort = (campo) => {
    setSort((prev) => {
      if (prev.campo !== campo) return { campo, direccion: "asc" };
      return { campo, direccion: prev.direccion === "asc" ? "desc" : "asc" };
    });
  };

  // ------------------ SELECCIÓN ------------------
  const [selectedKeys, setSelectedKeys] = useState([]);
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const toggleSelected = useCallback((row) => {
    setSelectedKeys((prev) => {
      const s = new Set(prev);
      if (s.has(row.key)) s.delete(row.key);
      else s.add(row.key);
      return Array.from(s);
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedKeys([]), []);

  // ------------------ FILAS ------------------
  const modalidadOptions = useMemo(
    () => listarModalidades(eventSubEventsRegistryState),
    [eventSubEventsRegistryState]
  );

  const categoriaOptions = useMemo(
    () => listarCategoriasDeModalidad(eventSubEventsRegistryState, filters.modalidad),
    [eventSubEventsRegistryState, filters.modalidad]
  );

  const enrichedRows = useMemo(() => {
    const list = Object.values(eventParticipantsState || {});
    return list.map((r) => ({
      ...r,
      __modalidadLabel: resolverModalidadLabel(eventSubEventsRegistryState, r.modalidad),
      __categoriaLabel: resolverCategoriaLabel(
        eventSubEventsRegistryState,
        r.modalidad,
        r.rama,
        r.categoria
      ),
      __ramaLabel: NOMBRE_RAMA[r.rama] || r.rama || "",
    }));
  }, [eventParticipantsState, eventSubEventsRegistryState]);

  const filteredRows = useMemo(() => {
    const nTxt = safeLower(filters.numeroText).trim();
    const nombreTxt = safeLower(filters.nombreText).trim();
    const correoTxt = safeLower(filters.correoText).trim();
    const inspTxt = safeLower(inspeccionTexto).trim();

    return enrichedRows.filter((r) => {
      if (nTxt && !safeLower(r.numero).includes(nTxt)) return false;
      if (
        nombreTxt &&
        !safeLower(r.nombres + " " + r.apellidos).includes(nombreTxt)
      ) {
        return false;
      }
      if (correoTxt && !safeLower(r.correo).includes(correoTxt)) return false;

      if (filters.modalidad !== "" && String(r.modalidad) !== String(filters.modalidad)) {
        return false;
      }
      if (filters.rama !== "" && r.rama !== filters.rama) return false;

      if (filters.categoriaValor) {
        const [ramaSel, catSel] = filters.categoriaValor.split("|");
        if (String(r.categoria) !== String(catSel) || r.rama !== ramaSel) return false;
      }

      if (campoInspeccion) {
        const valor = r[campoInspeccion];
        const tieneValor = valor !== undefined && valor !== null && valor !== "";
        if (inspeccionModo === "conValor" && !tieneValor) return false;
        if (inspeccionModo === "sinValor" && tieneValor) return false;
        if (inspeccionModo === "contiene" && !safeLower(valor).includes(inspTxt)) {
          return false;
        }
      }

      return true;
    });
  }, [enrichedRows, filters, campoInspeccion, inspeccionModo, inspeccionTexto]);

  const sortedRows = useMemo(() => {
    const campo = sort.campo;
    const dir = sort.direccion === "asc" ? 1 : -1;
    return filteredRows.slice().sort((a, b) => {
      let av;
      let bv;
      if (campo === "numero") {
        av = Number(a.numero) || 0;
        bv = Number(b.numero) || 0;
      } else if (campo === "modalidad") {
        av = safeLower(a.__modalidadLabel);
        bv = safeLower(b.__modalidadLabel);
      } else if (campo === "categoria") {
        av = safeLower(a.__categoriaLabel);
        bv = safeLower(b.__categoriaLabel);
      } else if (campo === "campoInspeccion") {
        av = safeLower(a[campoInspeccion]);
        bv = safeLower(b[campoInspeccion]);
      } else {
        av = safeLower(a[campo]);
        bv = safeLower(b[campo]);
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filteredRows, sort, campoInspeccion]);

  const selectAllVisible = useCallback(() => {
    setSelectedKeys((prev) => {
      const s = new Set(prev);
      sortedRows.forEach((r) => s.add(r.key));
      return Array.from(s);
    });
  }, [sortedRows]);

  const unselectAllVisible = useCallback(() => {
    setSelectedKeys((prev) => {
      const s = new Set(prev);
      sortedRows.forEach((r) => s.delete(r.key));
      return Array.from(s);
    });
  }, [sortedRows]);

  const headerAllChecked =
    sortedRows.length > 0 && sortedRows.every((r) => selectedSet.has(r.key));
  const headerIndeterminate =
    sortedRows.some((r) => selectedSet.has(r.key)) && !headerAllChecked;

  // ------------------ DIALOGO DE EDICIÓN MASIVA ------------------
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tipoOperacion, setTipoOperacion] = useState("");
  const [campoOrigen, setCampoOrigen] = useState("");
  const [campoDestino, setCampoDestino] = useState("");
  const [nuevaCategoriaValor, setNuevaCategoriaValor] = useState(""); // "rama|cat"
  const [nuevoGenero, setNuevoGenero] = useState("");
  const [campoLibreNombre, setCampoLibreNombre] = useState("");
  const [campoLibreValor, setCampoLibreValor] = useState("");
  const [codigoDescuentoCodigo, setCodigoDescuentoCodigo] = useState("");
  const [codigoDescuentoMonto, setCodigoDescuentoMonto] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [logs, setLogs] = useState([]);

  const log = (msg) => setLogs((prev) => [...prev, msg]);

  const parametrosOperacion = useMemo(() => {
    if (tipoOperacion === "renombrar") return { campoOrigen, campoDestino };
    if (tipoOperacion === "categoria") {
      const [rama, categoria] = nuevaCategoriaValor
        ? nuevaCategoriaValor.split("|")
        : [null, null];
      return { nuevaCategoria: categoria, nuevaRama: rama };
    }
    if (tipoOperacion === "genero") return { nuevoGenero };
    if (tipoOperacion === "campoLibre") {
      return { campo: campoLibreNombre, valor: campoLibreValor };
    }
    if (tipoOperacion === "agregarCodigoDescuento") {
      return { codigo: codigoDescuentoCodigo, monto: codigoDescuentoMonto };
    }
    return {};
  }, [
    tipoOperacion,
    campoOrigen,
    campoDestino,
    nuevaCategoriaValor,
    nuevoGenero,
    campoLibreNombre,
    campoLibreValor,
    codigoDescuentoCodigo,
    codigoDescuentoMonto,
  ]);

  const errorValidacion = validarParametrosOperacion(tipoOperacion, parametrosOperacion);

  const selectedRows = useMemo(() => {
    const map = new Map(enrichedRows.map((r) => [r.key, r]));
    return selectedKeys.map((k) => map.get(k)).filter(Boolean);
  }, [enrichedRows, selectedKeys]);

  const atributosDisponibles = useMemo(
    () => listarAtributosDisponibles(selectedRows),
    [selectedRows]
  );

  const previewCambios = useMemo(() => {
    if (!tipoOperacion || errorValidacion) return [];
    return selectedRows.map((row) => ({
      row,
      resultado: construirCambio(tipoOperacion, row, parametrosOperacion),
    }));
  }, [selectedRows, tipoOperacion, parametrosOperacion, errorValidacion]);

  const totalCambian = previewCambios.filter((p) => p.resultado.cambia).length;

  function resetOperacion() {
    setTipoOperacion("");
    setCampoOrigen("");
    setCampoDestino("");
    setNuevaCategoriaValor("");
    setNuevoGenero("");
    setCampoLibreNombre("");
    setCampoLibreValor("");
    setCodigoDescuentoCodigo("");
    setCodigoDescuentoMonto("");
    setConfirmando(false);
  }

  function cerrarDialogo() {
    if (aplicando) return;
    setBulkOpen(false);
    resetOperacion();
    setLogs([]);
    setProgreso(0);
  }

  async function ejecutarAplicar() {
    if (totalCambian === 0) return;
    setAplicando(true);
    setProgreso(0);
    setLogs([]);

    const basePath = `${fb.EventosCompletos}/${eventoIndex}/${fb.inscritos}${fb.listadoInscritos}`;
    const registrosACambiar = previewCambios.filter((p) => p.resultado.cambia);

    log(`Total de inscritos a actualizar: ${registrosACambiar.length}`);

    // Se agrupa por REGISTRO (no por ruta suelta), para que todos los campos
    // de un mismo inscrito -incluido su changesRegistry- viajen en el mismo
    // grupo. Así un inscrito nunca queda a medio actualizar entre dos grupos.
    const grupos = [];
    for (let i = 0; i < registrosACambiar.length; i += BATCH_SIZE) {
      grupos.push(registrosACambiar.slice(i, i + BATCH_SIZE));
    }

    for (let i = 0; i < grupos.length; i++) {
      const updatesGrupo = {};
      grupos[i].forEach(({ row, resultado }) => {
        Object.entries(resultado.updates).forEach(([campo, valor]) => {
          updatesGrupo[`${basePath}${row.key}/${campo}`] = valor;
        });
        const changesRegistry = fb.changesRegistryFunc(
          row.key,
          row,
          userID,
          atleteAccount,
          14,
          `Edición masiva: ${resultado.resumen}`
        );
        updatesGrupo[`${basePath}${row.key}/changesRegistry/`] = changesRegistry;
      });

      // eslint-disable-next-line no-await-in-loop
      await fb.db.ref().update(updatesGrupo);
      const pct = Math.round(((i + 1) / grupos.length) * 100);
      setProgreso(pct);
      log(`Grupo ${i + 1} de ${grupos.length} aplicado (${grupos[i].length} inscritos)`);
      if (i + 1 < grupos.length) {
        // eslint-disable-next-line no-await-in-loop
        await delay(BATCH_DELAY_MS);
      }
    }

    log("Listo.");
    setAplicando(false);
    setConfirmando(false);
    clearSelection();
  }

  // ------------------ POPOVER FILTROS ------------------
  const renderFilterPopoverContent = () => {
    const col = filterUI.col;
    const set = (patch) => setFilters((p) => ({ ...p, ...patch }));
    if (!col) return null;

    return (
      <Box p={2} style={{ maxWidth: 360 }}>
        <Typography variant="subtitle1" gutterBottom>
          Filtro: <b>{col}</b>
        </Typography>

        {col === "numero" && (
          <TextField
            label="Folio contiene"
            value={filters.numeroText}
            onChange={(e) => set({ numeroText: e.target.value })}
            fullWidth
          />
        )}

        {col === "nombre" && (
          <TextField
            label="Nombre o apellido contiene"
            value={filters.nombreText}
            onChange={(e) => set({ nombreText: e.target.value })}
            fullWidth
          />
        )}

        {col === "correo" && (
          <TextField
            label="Correo contiene"
            value={filters.correoText}
            onChange={(e) => set({ correoText: e.target.value })}
            fullWidth
          />
        )}

        {col === "modalidad" && (
          <FormControl fullWidth>
            <InputLabel>Modalidad</InputLabel>
            <Select
              value={filters.modalidad}
              onChange={(e) => set({ modalidad: e.target.value, categoriaValor: "" })}
            >
              <MenuItem value="">Todas</MenuItem>
              {modalidadOptions.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {col === "categoria" && (
          <FormControl fullWidth disabled={!filters.modalidad}>
            <InputLabel>Categoría</InputLabel>
            <Select
              value={filters.categoriaValor}
              onChange={(e) => set({ categoriaValor: e.target.value })}
            >
              <MenuItem value="">Todas</MenuItem>
              {categoriaOptions.map((c) => (
                <MenuItem key={c.rama + "|" + c.value} value={c.rama + "|" + c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
            {!filters.modalidad && (
              <Typography variant="caption" style={{ marginTop: 4 }}>
                Filtra primero por modalidad
              </Typography>
            )}
          </FormControl>
        )}

        {col === "rama" && (
          <FormControl fullWidth>
            <InputLabel>Género</InputLabel>
            <Select value={filters.rama} onChange={(e) => set({ rama: e.target.value })}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="V">Varonil</MenuItem>
              <MenuItem value="F">Femenil</MenuItem>
              <MenuItem value="M">Mixto</MenuItem>
            </Select>
          </FormControl>
        )}

        {col === "campoInspeccion" && (
          <Box display="grid" gridGap={12}>
            <FormControl fullWidth>
              <InputLabel>Mostrar</InputLabel>
              <Select
                value={inspeccionModo}
                onChange={(e) => setInspeccionModo(e.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="conValor">Con valor</MenuItem>
                <MenuItem value="sinValor">Sin valor</MenuItem>
                <MenuItem value="contiene">Contiene texto...</MenuItem>
              </Select>
            </FormControl>
            {inspeccionModo === "contiene" && (
              <TextField
                label="Contiene"
                value={inspeccionTexto}
                onChange={(e) => setInspeccionTexto(e.target.value)}
                fullWidth
              />
            )}
          </Box>
        )}

        <Box mt={2} display="flex" gap={8} justifyContent="flex-end">
          <Button
            onClick={() => {
              if (col === "numero") set({ numeroText: "" });
              if (col === "nombre") set({ nombreText: "" });
              if (col === "correo") set({ correoText: "" });
              if (col === "modalidad") set({ modalidad: "", categoriaValor: "" });
              if (col === "categoria") set({ categoriaValor: "" });
              if (col === "rama") set({ rama: "" });
              if (col === "campoInspeccion") {
                setInspeccionModo("todos");
                setInspeccionTexto("");
              }
            }}
          >
            Limpiar
          </Button>
          <Button variant="contained" color="primary" onClick={closeFilter}>
            Cerrar
          </Button>
        </Box>
      </Box>
    );
  };

  function renderSortIcon(campo) {
    if (sort.campo !== campo) return null;
    return sort.direccion === "asc" ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  }

  return (
    <Box>
      <Box mb={1}>
        <Typography variant="h4">Edición masiva de inscritos</Typography>
        <Typography variant="body2" style={{ opacity: 0.75 }}>
          Total: {enrichedRows.length} | Filtrados: {filteredRows.length} | Seleccionados:{" "}
          {selectedKeys.length}
        </Typography>
      </Box>

      <Box mb={2} display="flex" gap={12} flexWrap="wrap" alignItems="center">
        <TextField
          label="Inspeccionar campo (clave exacta)"
          placeholder="ej. talladeplayera"
          value={campoInspeccion}
          onChange={(e) => setCampoInspeccion(e.target.value)}
          style={{ minWidth: 260 }}
        />

        <Button
          startIcon={<EditIcon />}
          variant="contained"
          color="secondary"
          disabled={selectedKeys.length === 0}
          onClick={() => setBulkOpen(true)}
        >
          Editar seleccionados
        </Button>

        <Box flexGrow={1} />

        <Button variant="outlined" onClick={clearSelection} disabled={selectedKeys.length === 0}>
          Limpiar selección
        </Button>

        <Button
          variant="outlined"
          onClick={() => (headerAllChecked ? unselectAllVisible() : selectAllVisible())}
          disabled={sortedRows.length === 0}
        >
          {headerAllChecked ? "Deseleccionar visibles" : "Seleccionar visibles"}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                onClick={() => (headerAllChecked ? unselectAllVisible() : selectAllVisible())}
                style={{ cursor: "pointer" }}
              >
                <Checkbox
                  checked={headerAllChecked}
                  indeterminate={headerIndeterminate}
                  tabIndex={-1}
                  disableRipple
                  onClick={(e) => e.preventDefault()}
                  onMouseDown={(e) => e.preventDefault()}
                />
              </TableCell>

              <TableCell className={classes.headCell}>
                <span className={classes.headTitleWrap} onClick={() => toggleSort("numero")}>
                  Folio {renderSortIcon("numero")}
                  <IconButton size="small" onClick={openFilter("numero")}>
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </span>
              </TableCell>

              <TableCell className={classes.headCell}>
                <span className={classes.headTitleWrap} onClick={() => toggleSort("nombres")}>
                  Nombre {renderSortIcon("nombres")}
                  <IconButton size="small" onClick={openFilter("nombre")}>
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </span>
              </TableCell>

              <TableCell className={classes.headCell}>
                <span className={classes.headTitleWrap} onClick={() => toggleSort("correo")}>
                  Correo {renderSortIcon("correo")}
                  <IconButton size="small" onClick={openFilter("correo")}>
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </span>
              </TableCell>

              <TableCell className={classes.headCell}>
                <span className={classes.headTitleWrap} onClick={() => toggleSort("modalidad")}>
                  Modalidad {renderSortIcon("modalidad")}
                  <IconButton size="small" onClick={openFilter("modalidad")}>
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </span>
              </TableCell>

              <TableCell className={classes.headCell}>
                <span className={classes.headTitleWrap} onClick={() => toggleSort("categoria")}>
                  Categoría {renderSortIcon("categoria")}
                  <IconButton size="small" onClick={openFilter("categoria")}>
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </span>
              </TableCell>

              <TableCell className={classes.headCell}>
                <span className={classes.headTitleWrap} onClick={() => toggleSort("rama")}>
                  Género {renderSortIcon("rama")}
                  <IconButton size="small" onClick={openFilter("rama")}>
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </span>
              </TableCell>

              {campoInspeccion && (
                <TableCell className={classes.headCell}>
                  <span
                    className={classes.headTitleWrap}
                    onClick={() => toggleSort("campoInspeccion")}
                  >
                    {campoInspeccion} {renderSortIcon("campoInspeccion")}
                    <IconButton size="small" onClick={openFilter("campoInspeccion")}>
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </span>
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.map((row) => {
              const selected = selectedSet.has(row.key);
              return (
                <TableRow
                  key={row.key}
                  hover
                  className={selected ? classes.rowSelected : ""}
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSelected(row)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected}
                      tabIndex={-1}
                      disableRipple
                      onClick={(e) => e.preventDefault()}
                      onMouseDown={(e) => e.preventDefault()}
                    />
                  </TableCell>
                  <TableCell>{row.numero}</TableCell>
                  <TableCell>
                    {row.nombres} {row.apellidos}
                  </TableCell>
                  <TableCell>{row.correo}</TableCell>
                  <TableCell>{row.__modalidadLabel}</TableCell>
                  <TableCell>{row.__categoriaLabel}</TableCell>
                  <TableCell>{row.__ramaLabel}</TableCell>
                  {campoInspeccion && (
                    <TableCell>{String(row[campoInspeccion] ?? "")}</TableCell>
                  )}
                </TableRow>
              );
            })}

            {sortedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={campoInspeccion ? 8 : 7}>
                  <Typography variant="body2">
                    No hay inscritos con los filtros actuales.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={popoverOpen}
        anchorEl={filterUI.anchorEl}
        onClose={closeFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {renderFilterPopoverContent()}
      </Popover>

      <Dialog open={bulkOpen} onClose={cerrarDialogo} maxWidth="md" fullWidth>
        <DialogTitle>Editar seleccionados ({selectedKeys.length})</DialogTitle>

        <DialogContent dividers>
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              Tipo de operación
            </Typography>
            <FormControl className={classes.smallSelect}>
              <InputLabel>Operación</InputLabel>
              <Select
                value={tipoOperacion}
                onChange={(e) => {
                  resetOperacion();
                  setTipoOperacion(e.target.value);
                }}
                disabled={aplicando}
              >
                <MenuItem value="">Selecciona...</MenuItem>
                {Object.entries(TIPOS_OPERACION).map(([key, op]) => (
                  <MenuItem key={key} value={key}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {tipoOperacion === "renombrar" && (
              <Box display="flex" gap={12} mt={2} flexWrap="wrap">
                <Autocomplete
                  options={atributosDisponibles}
                  value={campoOrigen || null}
                  onChange={(e, nuevoValor) => setCampoOrigen(nuevoValor || "")}
                  disabled={aplicando}
                  style={{ minWidth: 260 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Campo origen"
                      helperText={
                        atributosDisponibles.length === 0
                          ? "Los inscritos seleccionados no tienen atributos disponibles para renombrar"
                          : undefined
                      }
                    />
                  )}
                />
                <TextField
                  label="Campo destino"
                  value={campoDestino}
                  onChange={(e) => setCampoDestino(e.target.value)}
                  disabled={aplicando}
                  style={{ minWidth: 240 }}
                />
              </Box>
            )}

            {tipoOperacion === "categoria" && (
              <Box mt={2}>
                {!filters.modalidad && (
                  <Typography variant="body2" color="error">
                    Filtra la tabla por una modalidad (arriba, columna Modalidad) para poder
                    elegir la categoría destino.
                  </Typography>
                )}
                {filters.modalidad && (
                  <FormControl className={classes.smallSelect}>
                    <InputLabel>Nueva categoría</InputLabel>
                    <Select
                      value={nuevaCategoriaValor}
                      onChange={(e) => setNuevaCategoriaValor(e.target.value)}
                      disabled={aplicando}
                    >
                      <MenuItem value="">Selecciona...</MenuItem>
                      {categoriaOptions.map((c) => (
                        <MenuItem key={c.rama + "|" + c.value} value={c.rama + "|" + c.value}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}

            {tipoOperacion === "genero" && (
              <Box mt={2}>
                <FormControl className={classes.smallSelect}>
                  <InputLabel>Nuevo género</InputLabel>
                  <Select
                    value={nuevoGenero}
                    onChange={(e) => setNuevoGenero(e.target.value)}
                    disabled={aplicando}
                  >
                    <MenuItem value="">Selecciona...</MenuItem>
                    <MenuItem value="V">Varonil</MenuItem>
                    <MenuItem value="F">Femenil</MenuItem>
                    <MenuItem value="M">Mixto</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {tipoOperacion === "campoLibre" && (
              <Box display="flex" gap={12} mt={2} flexWrap="wrap">
                <TextField
                  label="Campo"
                  value={campoLibreNombre}
                  onChange={(e) => setCampoLibreNombre(e.target.value)}
                  disabled={aplicando}
                  style={{ minWidth: 240 }}
                />
                <TextField
                  label="Valor"
                  value={campoLibreValor}
                  onChange={(e) => setCampoLibreValor(e.target.value)}
                  disabled={aplicando}
                  style={{ minWidth: 240 }}
                />
              </Box>
            )}

            {tipoOperacion === "agregarCodigoDescuento" && (
              <Box display="flex" gap={12} mt={2} flexWrap="wrap">
                <TextField
                  label="Código de descuento"
                  value={codigoDescuentoCodigo}
                  onChange={(e) => setCodigoDescuentoCodigo(e.target.value)}
                  disabled={aplicando}
                  style={{ minWidth: 240 }}
                />
                <TextField
                  label="Monto del descuento ($)"
                  type="number"
                  value={codigoDescuentoMonto}
                  onChange={(e) => setCodigoDescuentoMonto(e.target.value)}
                  disabled={aplicando}
                  style={{ minWidth: 200 }}
                />
                <Typography variant="body2" style={{ width: "100%", opacity: 0.7 }}>
                  Se agrega como un ítem más de la orden (igual que si se
                  hubiera aplicado durante el registro). Un inscrito que ya
                  tenga este mismo código aplicado se omite automáticamente.
                </Typography>
              </Box>
            )}
          </Box>

          {tipoOperacion && !errorValidacion && (
            <>
              <Divider />
              <Box mt={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Vista previa — se actualizarán {totalCambian} de {selectedRows.length}{" "}
                  seleccionados
                </Typography>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Folio</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Resultado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewCambios.slice(0, 200).map(({ row, resultado }) => (
                      <TableRow key={row.key}>
                        <TableCell>{row.numero}</TableCell>
                        <TableCell>
                          {row.nombres} {row.apellidos}
                        </TableCell>
                        <TableCell>
                          {resultado.cambia ? (
                            resultado.resumen
                          ) : (
                            <Typography variant="body2" style={{ opacity: 0.6 }}>
                              Sin cambios ({resultado.motivo})
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {previewCambios.length > 200 && (
                  <Typography variant="caption" style={{ opacity: 0.7 }}>
                    Mostrando 200 de {previewCambios.length}
                  </Typography>
                )}
              </Box>
            </>
          )}

          {tipoOperacion && errorValidacion && (
            <Typography variant="body2" color="error" style={{ marginTop: 16 }}>
              {errorValidacion}
            </Typography>
          )}

          {aplicando && (
            <Box mt={2}>
              <LinearProgress variant="determinate" value={progreso} />
              <Typography variant="body2" style={{ marginTop: 8 }}>
                {progreso}% completado
              </Typography>
              <Box
                mt={1}
                style={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: 4,
                  padding: 10,
                  maxHeight: 160,
                  overflowY: "auto",
                  fontFamily: "monospace",
                  fontSize: 13,
                }}
              >
                {logs.map((l, i) => (
                  <div key={i}>{l}</div>
                ))}
              </Box>
            </Box>
          )}

          {confirmando && !aplicando && (
            <Typography
              variant="body2"
              color="error"
              style={{ marginTop: 16, fontWeight: "bold" }}
            >
              ¿Confirmas aplicar este cambio a {totalCambian} inscritos? Esta acción no se puede
              deshacer automáticamente.
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={cerrarDialogo} disabled={aplicando}>
            Cerrar
          </Button>
          {!confirmando && (
            <Button
              variant="contained"
              color="primary"
              disabled={!tipoOperacion || !!errorValidacion || totalCambian === 0 || aplicando}
              onClick={() => setConfirmando(true)}
            >
              Aplicar cambios
            </Button>
          )}
          {confirmando && (
            <Button
              variant="contained"
              color="secondary"
              disabled={aplicando}
              onClick={ejecutarAplicar}
            >
              Sí, aplicar a {totalCambian} inscritos
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
